import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import {
  Component,
  inject,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { faAward } from '@fortawesome/pro-solid-svg-icons';
import {
  AaNgModule,
  ActivityIconPipe,
  ActivityType,
  CarouselItem,
  DisplayStrategy,
  ICellDefinition,
  IColumnHeaderDefinition,
  INSTITUTION_CONTEXT_TOKEN,
  IRowDefinition,
  ISTArticle,
  ISTGrant,
  ISTPatent,
  ISTPersonActivity,
  ObjectPropertyDisplayStrategy,
  PersonNamePipe,
  StripHtmlPipe,
} from 'aa-ng';
import { QuicklinkDirective } from 'ngx-quicklink';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  take,
} from 'rxjs/operators';
import { IHiddenFields } from 'src/app/models/hidden-fields';

import {
  IInstitution,
  IInstitutionContext,
  IResearchAtAGlanceMetric,
  IUnit,
  IUnitConfiguration,
  NavigationTier,
  UnitConfigurations,
  UnitType,
} from '../../../models';
import { ResearchAtAGlanceActivityTypeTitlePipe } from '../../pipes';
import { PersonUrlPipe } from '../../pipes/person-url.pipe';
import {
  ActivitiesService,
  DashboardService,
  InstitutionsService,
  LoadingIndicatorService,
} from '../../services';
import { BaseDirective } from '../base.component';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';

interface IExtendedActivity extends ISTPersonActivity {
  collaboratorsAreExpanded?: boolean;
}

const DOI_URL = 'https://doi.org/';

const CLINICAL_TRIAL_URL = 'https://clinicaltrials.gov/ct2/show/';

@Component({
  selector: 'app-research-at-a-glance',
  imports: [
    AaNgModule,
    AsyncPipe,
    BreadcrumbComponent,
    NgClass,
    PersonUrlPipe,
    QuicklinkDirective,
    RouterLink,
  ],
  templateUrl: './research-at-a-glance.component.html',
  styleUrls: ['./research-at-a-glance.component.scss'],
})
export class ResearchAtAGlanceComponent extends BaseDirective {
  private dashboardService = inject(DashboardService);
  private activitiesService = inject(ActivitiesService);
  private activityIconPipe = inject(ActivityIconPipe);
  private activityTypeTitlePipe = inject(
    ResearchAtAGlanceActivityTypeTitlePipe,
  );
  private numberPipe = inject(DecimalPipe);
  private loadingIndicatorService = inject(LoadingIndicatorService);
  public personNamePipe = inject(PersonNamePipe);
  public stripHtmlPipe = inject(StripHtmlPipe);
  institutionsService = inject<InstitutionsService>(INSTITUTION_CONTEXT_TOKEN);

  ActivityType = ActivityType;
  selectedActivityObj = new BehaviorSubject<ActivityType>(null);
  selectedActivityName = '';
  _context: IInstitutionContext;
  currentInstitutionIds: number[];
  currentUnitId: number;
  _unitConfigBehaviorSubject = new BehaviorSubject<IUnitConfiguration[]>([]);
  _unitConfigSubscription: Subscription;
  _metricsSubscription: Subscription;
  _recentActivitiesSubscription: Subscription;
  pageSize = 20;
  currentPage = 1;
  endPage = 1;
  viewAllSelected = false;
  viewButtonText: string;

  columnHeaderDefinitions = new BehaviorSubject<IColumnHeaderDefinition[]>([]);
  rowDefinition = new BehaviorSubject<IRowDefinition<ISTPersonActivity>>(null);

  headingText = 'Research at a Glance';
  subHeadingLevelText = '';
  researchActivitiesText = '';
  metricResults: IResearchAtAGlanceMetric[] = [];
  results = new BehaviorSubject<IExtendedActivity[]>(null);
  hideResearchAtAGlance = false;
  overriddenResearchAtAGlanceTypes = new BehaviorSubject<number[]>(null);
  carouselItems = new BehaviorSubject<CarouselItem[]>(null);
  activityNameOverride: { [key: string]: string };
  metricItemsSorted: boolean = false;

  hiddenFields: IHiddenFields;

  @Input() set unitConfigurations(unitConfigs: IUnitConfiguration[]) {
    this._unitConfigBehaviorSubject.next(unitConfigs ? unitConfigs : []);
  }

  @ViewChild('scholarsCell')
  scholarsCellTemplate: TemplateRef<ISTPersonActivity>;
  @ViewChild('unitsCell') unitsCellTemplate: TemplateRef<ISTPersonActivity>;

  constructor() {
    super();

    this._subscriptions.push(
      this.institutionsService.context.subscribe(
        (context) => (this._context = context),
      ),
      this.institutionsService.hiddenFields.subscribe(
        (hf) => (this.hiddenFields = hf),
      ),
      combineLatest([
        this.dashboardService.state,
        this.selectedActivityObj,
        this._unitConfigBehaviorSubject,
      ])
        .pipe(
          debounceTime(100),
          map(([state, selectedActivity, unitConfigs]) => {
            if (state) {
              const institutionIds = state.activeInstitutions.map((i) => i.id);
              const unitId =
                state && state.activeUnit ? state.activeUnit.id : null;
              this.currentInstitutionIds = institutionIds;
              this.currentUnitId = unitId;

              this.hideResearchAtAGlance = false;
              this.overriddenResearchAtAGlanceTypes.next(null);

              if (state.navLevel === NavigationTier.Unit) {
                if (this._unitConfigSubscription) {
                  this._unitConfigSubscription.unsubscribe();
                }
                if (
                  unitConfigs.filter(
                    (c) => c.key === UnitConfigurations.HideResearchAtAGlance,
                  ).length > 0
                ) {
                  this.hideResearchAtAGlance =
                    unitConfigs
                      .find(
                        (c) =>
                          c.key === UnitConfigurations.HideResearchAtAGlance,
                      )
                      .value.toLowerCase() === 'true';
                }
                if (
                  unitConfigs.filter(
                    (c) =>
                      c.key ===
                      UnitConfigurations.OverriddenUnitResearchAtAGlanceTypes,
                  ).length > 0
                ) {
                  const t = unitConfigs.find(
                    (c) =>
                      c.key ===
                      UnitConfigurations.OverriddenUnitResearchAtAGlanceTypes,
                  ).value as string;
                  const test = t.split(',').map((x) => +x);
                  this.overriddenResearchAtAGlanceTypes.next(test);
                } else {
                  this.overriddenResearchAtAGlanceTypes.next(null);
                }
              }

              let displayOrder = [
                3, 4, 2, 6, 7, 60, 1, 12, 31, 30, 32, 33, 34, 40, 41, 35, 13,
                14, 36, 37, 50, 51, 70, 71, 72,
              ];
              let raagTypes = [
                3, 4, 2, 6, 7, 60, 1, 12, 31, 30, 32, 33, 34, 40, 41, 35, 13,
                14, 36, 37, 50, 51, 70, 71, 72,
              ];

              if (this._context.useNewEducationalMaterialType) {
                displayOrder = [
                  3, 4, 2, 6, 7, 60, 1, 12, 31, 30, 32, 33, 34, 129, 35, 13, 14,
                  36, 37, 50, 51, 70, 71, 72,
                ];
                raagTypes = [
                  3, 4, 2, 6, 7, 60, 1, 12, 31, 30, 32, 33, 34, 129, 35, 13, 14,
                  36, 37, 50, 51, 70, 71, 72,
                ];
              }
              if (
                this._context.overriddenResearchAtAGlanceTypes ||
                this.overriddenResearchAtAGlanceTypes.value
              ) {
                raagTypes =
                  this.overriddenResearchAtAGlanceTypes.value ?
                    this.overriddenResearchAtAGlanceTypes.value
                  : this._context.overriddenResearchAtAGlanceTypes;
              }
              if (
                this._context.researchAtAGlanceWorkTypeOrder &&
                this._context.researchAtAGlanceWorkTypeOrder.length > 0
              ) {
                displayOrder = this._context.researchAtAGlanceWorkTypeOrder;
              }

              if (state.navLevel === NavigationTier.Unit) {
                this.headingText =
                  state.activeUnit.name +
                  (this._context.useCombinedSystemDashboard ?
                    ` - ${state.activeInstitutions[0].shortName}`
                  : '');
                this.subHeadingLevelText = state.activeUnit.shortName;
              } else if (state.navLevel === NavigationTier.Institution) {
                if (this.dashboardService.isSystem) {
                  const activeInst = state.activeInstitutions[0];
                  this.headingText = activeInst.name;
                  this.subHeadingLevelText =
                    this.dashboardService.dashboardShortName;
                } else {
                  this.headingText =
                    this._context.researchAtAGlanceHeading ?
                      this._context.researchAtAGlanceHeading
                    : 'Research at a' + ' Glance';
                  this.subHeadingLevelText =
                    this.dashboardService.dashboardShortName;
                }
              } else {
                this.headingText =
                  this._context.researchAtAGlanceHeading ?
                    this._context.researchAtAGlanceHeading
                  : 'Research at a Glance';
                this.subHeadingLevelText =
                  this.dashboardService.dashboardShortName;
              }

              this.researchActivitiesText =
                this._context.researchActivitiesText ?
                  this._context.researchActivitiesText
                : 'Research activities for';

              this.activityNameOverride = this._context.activityNameOverride;

              if (this._metricsSubscription) {
                this._metricsSubscription.unsubscribe();
              }

              this._metricsSubscription = this.activitiesService
                .getMetrics(
                  raagTypes,
                  institutionIds,
                  unitId,
                  this._context.excludedGrantStatuses,
                  true,
                )
                .pipe(
                  take(1),
                  finalize(() => {
                    this.loadingIndicatorService.forceLoadingState(false);
                  }),
                )
                .subscribe((metricResults) => {
                  let newMetricItems =
                    this.hideResearchAtAGlance ?
                      []
                    : metricResults
                        .filter((t) => t.metricResult > 0)
                        .map((r) => {
                          const activityTypeTitle =
                            this.activityTypeTitlePipe.transform(
                              r.activityTypeId,
                            );

                          return new CarouselItem(
                            this.activityNameOverride[
                              ActivityType[r.activityTypeId].toString()
                            ] || activityTypeTitle,

                            this.formatMetric(r),
                            r.activityTypeId,
                            this.activityIconPipe.transform({
                              activityTypeId: r.activityTypeId,
                              id: 0,
                              guid: '',
                              personActivityRecordId: 0,
                              contributors: [],
                            }),
                          );
                        });

                  if (
                    this._context.researchAtAGlanceShowAdditionalGrantCountTile
                  ) {
                    const grantActivityMetrics = metricResults.find(
                      (x) => x.activityTypeId === ActivityType.Grant,
                    );
                    if (
                      grantActivityMetrics &&
                      grantActivityMetrics.recordCount > 0
                    ) {
                      newMetricItems.push(
                        new CarouselItem(
                          this.activityTypeTitlePipe.transform(
                            ActivityType.Grant,
                          ),
                          grantActivityMetrics.recordCount.toLocaleString(),
                          grantActivityMetrics.activityTypeId,
                          faAward,
                        ),
                      );
                    }
                  }

                  if (
                    this._context.researchAtAGlanceSortByCountDesc ||
                    (state.navLevel === NavigationTier.Unit &&
                      this._context
                        .orderResearchAtAGlanceTilesByDescendingInUnit)
                  ) {
                    const newMetricItemsNoDollar = newMetricItems.filter(
                      (metricItem) => !metricItem.metric.includes('$'),
                    );
                    const grantMetricDollar = newMetricItems.find(
                      (metricItem) =>
                        metricItem.metric.includes('$') &&
                        metricItem.rootItemId === 3,
                    );
                    newMetricItems = newMetricItemsNoDollar.sort((a, b) => {
                      return +b.metric - +a.metric;
                    });
                    if (grantMetricDollar) {
                      const grantIndex = newMetricItemsNoDollar.findIndex(
                        (i) => i.rootItemId === grantMetricDollar.rootItemId,
                      );
                      newMetricItems.splice(grantIndex, 0, grantMetricDollar);
                    }
                    if (
                      newMetricItems.length > 0 &&
                      this.selectedActivityObj.value !==
                        newMetricItems[0].rootItemId &&
                      !this.metricItemsSorted
                    ) {
                      this.metricItemsSorted = true;
                      this.updateSelectedActivityObjectToFirstMetricItem(
                        newMetricItems,
                      );
                    }
                  } else {
                    newMetricItems = newMetricItems.sort((a, b) => {
                      return (
                        displayOrder.indexOf(a.rootItemId) -
                        displayOrder.indexOf(b.rootItemId)
                      );
                    });
                  }

                  // if the results from getMetrics don't contain an entry for the currently selected activity type, change selected activity type to the first one that has a non-zero record count
                  if (
                    (this.selectedActivityObj.value === null ||
                      newMetricItems.find(
                        (t) => t.rootItemId === this.selectedActivityObj.value,
                      ) === undefined) &&
                    newMetricItems.length > 0
                  ) {
                    this.updateSelectedActivityObjectToFirstMetricItem(
                      newMetricItems,
                    );
                  }

                  const existingCarouselItems = JSON.stringify(
                    this.carouselItems.value,
                  );
                  const newCarouselItems = JSON.stringify(newMetricItems);
                  if (existingCarouselItems !== newCarouselItems) {
                    this.carouselItems.next(newMetricItems);
                  }

                  this.metricResults = metricResults;
                  this.viewButtonText = 'View More';
                });

              if (selectedActivity) {
                this.getRecentActivities(
                  0,
                  5,
                  selectedActivity,
                  institutionIds,
                  unitId,
                );
              }
              this.endPage = 1;
              this.viewAllSelected = false;
            }
          }),
        )
        .subscribe(),
    );
  }

  updateSelectedActivityObjectToFirstMetricItem(
    newMetricItems: CarouselItem[],
  ) {
    this.selectedActivityObj.next(newMetricItems[0].rootItemId);

    const activityTypeTitle = this.activityTypeTitlePipe.transform(
      this.selectedActivityObj.value,
    );
    this.selectedActivityName =
      this.activityNameOverride[
        ActivityType[this.selectedActivityObj.value].toString()
      ] || activityTypeTitle;
  }

  getRecentActivities(
    pageIndex: number,
    pageSize: number,
    selectedActivity: number,
    institutionIds: number[],
    unitId?: number,
  ) {
    if (
      this._recentActivitiesSubscription &&
      !this._recentActivitiesSubscription.closed
    ) {
      this.loadingIndicatorService.forceLoadingState(false);
      this._recentActivitiesSubscription.unsubscribe();
    }

    this.loadingIndicatorService.forceLoadingState(true);
    this._recentActivitiesSubscription = this.activitiesService
      .getRecent(
        pageIndex,
        pageSize,
        selectedActivity,
        institutionIds,
        unitId,
        this._context.excludedGrantStatuses,
      )
      .pipe(distinctUntilChanged())
      .subscribe({
        next: (activities: IExtendedActivity[]) => {
          this.determineColumnAndRowDefinitions(selectedActivity);
          activities.forEach((a) => {
            a.contributors = a.contributors.sort((b, c) =>
              b && c && b.lastName && c.lastName ?
                b.lastName.localeCompare(c.lastName)
              : 0,
            );
            if (a.contributors && a.contributors.length > 1) {
              a.collaboratorsAreExpanded = false;
            }
            if (a.title && a.title.length) {
              a.title = this.stripHtmlPipe.transform(a.title);
            }
          });
          this.results.next(activities);
          // Reinforce Current Page every time so we don't have to compare selectedActivity to previousActivity
          this.currentPage = pageIndex + 1;
          this.loadingIndicatorService.forceLoadingState(false);
        },
        error: (err) => {
          console.error(err);
          this.loadingIndicatorService.forceLoadingState(false);
        },
      });
  }

  selectActivity(activity: ActivityType) {
    this.selectedActivityObj.next(activity);

    const activityTypeTitle = this.activityTypeTitlePipe.transform(
      this.selectedActivityObj.value,
    );
    this.selectedActivityName =
      this.activityNameOverride[
        ActivityType[this.selectedActivityObj.value].toString()
      ] || activityTypeTitle;
  }

  selectUnit(unit: IUnit, institution: IInstitution) {
    if (unit && this._context.dashboardUnitTypes?.includes(unit.type)) {
      const selectableUnit = {
        id: unit.id,
        type: unit.type,
        name: `All ${unit.type in UnitType ? UnitType[unit.type] : 'Unit'}s`,
        tier: NavigationTier.Unit,
        parentId: institution.id,
      };

      this.dashboardService.selectUnit(selectableUnit);
    }
  }

  toggleViewAll(activityType: ActivityType) {
    this.viewAllSelected = !this.viewAllSelected;
    if (this.viewAllSelected) {
      this.viewButtonText = 'View Less';
      this.endPage = Math.trunc(
        (this.metricResults.find(
          (r) => r.activityTypeId === this.selectedActivityObj.value,
        )?.recordCount -
          1) /
          this.pageSize +
          1,
      );
      this.getRecentActivities(
        0,
        this.pageSize,
        activityType,
        this.currentInstitutionIds,
        this.currentUnitId,
      );
    } else {
      this.viewButtonText = 'View More';
      this.endPage = 1;
      this.getRecentActivities(
        0,
        5,
        activityType,
        this.currentInstitutionIds,
        this.currentUnitId,
      );
    }
  }

  updatePage(page: number) {
    this.getRecentActivities(
      page - 1,
      this.pageSize,
      this.selectedActivityObj.value,
      this.currentInstitutionIds,
      this.currentUnitId,
    );
  }

  openUrl(link: string) {
    window.open(link, '_blank');
  }

  getDoiUrl(id: string | number) {
    return `${DOI_URL}${id}`;
  }

  getTrialUrl(id: string | number) {
    return `${CLINICAL_TRIAL_URL}${id}`;
  }

  determineColumnAndRowDefinitions(activity: ActivityType) {
    let cellDefinitions: ICellDefinition<ISTPersonActivity>[] = [];
    let headerDefinitions: IColumnHeaderDefinition[] = [];
    switch (activity) {
      case ActivityType.Article:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Journal'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy: new ObjectPropertyDisplayStrategy<ISTArticle>(
              (result) => (result.outlet ? result.outlet : ''),
            ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Award:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '3fr' },
          {
            displayStrategy: new DisplayStrategy('Governing Society'),
            width: '3fr',
          },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Book:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Publisher'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor.trim() : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.BookChapter:
        headerDefinitions.push(
          {
            displayStrategy: new DisplayStrategy('Chapter Title'),
            width: '2fr',
          },
          { displayStrategy: new DisplayStrategy('Book Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Publisher'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.outlet ?
                  this.stripHtmlPipe.transform(result.outlet)
                : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.ClinicalTrial:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Sponsors'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.outlet ? result.outlet : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Exhibit:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Sponsor'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Patent:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '3fr' },
          { displayStrategy: new DisplayStrategy('Type'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '3fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy: new ObjectPropertyDisplayStrategy<ISTPatent>(
              (result) => (result.patentType ? result.patentType : ''),
            ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Grant: {
        const grantHeaderDefinitions: IColumnHeaderDefinition[] = [
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          {
            displayStrategy: new DisplayStrategy('Organization'),
            width: '1fr',
          },
          {
            displayStrategy: new DisplayStrategy('Total Amount'),
            width: '.5fr',
          },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        ];

        const grantCellDefinitions: ICellDefinition<ISTPersonActivity>[] = [
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy: new ObjectPropertyDisplayStrategy<ISTGrant>(
              (result) => (result.agencyName ? result.agencyName : ''),
            ),
          },
          {
            displayStrategy: new ObjectPropertyDisplayStrategy<ISTGrant>(
              (result) =>
                result.totalDollars ?
                  '$' + this.numberPipe.transform(result.totalDollars, '1.0-0')
                : '',
            ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        ];
        const hiddenGrantFields =
          this.hiddenFields && this.hiddenFields['Grant'] ?
            this.hiddenFields['Grant']
          : {};

        if (
          Object.prototype.hasOwnProperty.call(
            hiddenGrantFields,
            'totalDollars',
          )
        ) {
          grantHeaderDefinitions.splice(2, 1);
          grantCellDefinitions.splice(2, 1);
        }
        headerDefinitions = grantHeaderDefinitions;
        cellDefinitions = grantCellDefinitions;
        break;
      }
      case ActivityType.ConferenceProceeding:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Journal'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.outlet ? result.outlet : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Performance:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Sponsor'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Location'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.location ? result.location : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.LiteraryWork:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Production:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Sponsor'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Location'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.location ? result.location : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.OtherCreativeWork:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Sponsor'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.ClassroomEducationalMaterials:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          // { displayStrategy: new DisplayStrategy('Publisher'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.OnlineEducationalMaterials:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.EducationalMaterials:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.SoftwareDigitalMedia:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.OtherContent:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Publisher'), width: '1fr' },
          {
            displayStrategy: new DisplayStrategy('Journal Name'),
            width: '1fr',
          },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.sponsor ? result.sponsor : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.outlet ? result.outlet : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Presentation:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Venue'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.outlet ? result.outlet : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Technology:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.OtherIntellectualProperty:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Reviewer:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.CommunityEngagement:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          // { displayStrategy: new DisplayStrategy('Org'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          // {
          //   displayStrategy:
          //     new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
          //       (result) => (result.organization ? result.organization : ''),
          //     ),
          // },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Media:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          // { displayStrategy: new DisplayStrategy('Type'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          // {
          //   displayStrategy: new ObjectPropertyDisplayStrategy<IDisplayMedia>(
          //     (result) => (result.mediaType ? result.mediaType : ''),
          //   ),
          // },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.ResearchDataSet:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          // { displayStrategy: new DisplayStrategy('Repository'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          // {
          //   displayStrategy:
          //     new ObjectPropertyDisplayStrategy<IDisplayResearchDataSet>(
          //       (result) => (result.repository ? result.repository : ''),
          //     ),
          // },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.FieldResearch:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          // { displayStrategy: new DisplayStrategy('Role'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          // {
          //   displayStrategy:
          //     new ObjectPropertyDisplayStrategy<IDisplayFieldResearch>(
          //       (result) => (result.role ? result.role : ''),
          //     ),
          // },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
      case ActivityType.Project:
        headerDefinitions.push(
          { displayStrategy: new DisplayStrategy('Title'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Location'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Scholar(s)'), width: '1fr' },
          { displayStrategy: new DisplayStrategy('Unit'), width: '2fr' },
          { displayStrategy: new DisplayStrategy('Year'), width: '50px' },
        );
        cellDefinitions.push(
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.title ? this.stripHtmlPipe.transform(result.title) : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.location ? result.location : '',
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.scholarsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>(
                this.unitsCellTemplate,
              ),
          },
          {
            displayStrategy:
              new ObjectPropertyDisplayStrategy<ISTPersonActivity>((result) =>
                result.activityYear ? result.activityYear.toString() : '',
              ),
          },
        );
        break;
    }
    this.columnHeaderDefinitions.next(headerDefinitions);
    this.rowDefinition.next({ cellDefinitions });
  }

  private formatMetric(metric: IResearchAtAGlanceMetric): string {
    if (metric.activityTypeId === ActivityType.Grant) {
      if (!this._context.useGrantCountForRaaGTile) {
        return `$${this.numberFormatter(metric.metricResult)}`;
      } else {
        return this.numberPipe.transform(metric.recordCount, '1.0-0');
      }
    } else {
      return this.numberPipe.transform(metric.metricResult, '1.0-0');
    }
  }

  private numberFormatter(num: number) {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num;
  }
}
